import { FormSchema, OptionShape } from '@stormwater-software/lib-form';
import { UIControlType } from '@stormwater-software/lib-form-control-types';
import dayjs from 'dayjs';
import { dateFormat } from '../../../utils';
import React, { MouseEventHandler, useContext, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import ProjectContext from '../../../contexts/ProjectContext';
import ProjectPrintContext, {
  InspectionDateFilter,
  PrintProjectFormData,
} from '../../../contexts/ProjectPrintContext';
import { getMapOptionsSchema } from '../../Inspections/Details/SiteMapPrint';
import LoadingModal from '../../Shared/Loading/LoadingModal';
import PrintModal from '../../Shared/Print/PrintModal';
import { useProjectSiteMapInspections } from '../../../hooks/inspection';
import projectSiteMapInspectionApi from '../../../utils/api/projectSiteMapInspection';
import positionableApi from '../../../utils/api/positionable';
import {
  buildVersionOptions,
  MapVersion,
  scopeVersionsToFormType,
  versionDayOf,
} from '../../MapEditor/mapVersionOptions';


export type SiteMapSchemaOptions = {
  inspections: OptionShape[];
  /** The synthetic "Current Version" row, raw — it is recognised by `type`, not by label. */
  currentVersionInspection?: { id: number; created_date: any };
  versions?: OptionShape[];
  filter: InspectionDateFilter;
  filterOptions: OptionShape[];
  siteMapTextLabel: string;
  targetDocId?: number;
  targetDoc?: any;
  projectLevelEsriEnabled?: boolean;
};

/** The synthetic entry the API prepends to the inspection list; not a real inspection. */
export const CURRENT_VERSION_LABEL = 'Current Version';

export const getSiteMapFilterSchema = ({
  inspections,
  currentVersionInspection,
  versions = [],
  filter,
  filterOptions,
  siteMapTextLabel,
}: SiteMapSchemaOptions): FormSchema => {
  /**
   * SW2-2345 — "Current Version" stays reachable once versions take over the radio.
   *
   * That entry is synthetic and lives in the INSPECTION list, so when this modal switched
   * to listing versions it stopped being rendered at all — and with it the only way to
   * print the maps as they stand now. It is not a version day and never will be: the
   * newest version is the newest day something changed, which is not the same as today
   * for any map whose latest edit minted no version (a removal with an unparseable date,
   * a findings-only change).
   *
   * Pinned first, because it is the default a user reaches for, and because every other
   * row is a date and this one is not.
   */
  const currentVersionOption = currentVersionInspection
    ? {
        value: currentVersionInspection.id,
        // Built here rather than taken from `getInspectionOptions`. That helper labels an
        // inspection from `inspection_types`, and the synthetic entry carries only
        // `type` — so its rendered label is "<date> | " with nothing after the pipe, and
        // an earlier attempt to find it by label matched nothing at all. The label is
        // also what `validateInspectionData` reads the choice back from, so the name has
        // to be in it.
        label: `${dayjs.utc(currentVersionInspection.created_date).format(dateFormat)} | ${CURRENT_VERSION_LABEL}`,
      }
    : undefined;

  const choices = versions.length
    ? [...(currentVersionOption ? [currentVersionOption] : []), ...versions]
    : inspections;

  return {
    inspection_label: {
      label: 'Site Maps',
      controlType: UIControlType.plainText,
      style: { marginBottom: 0 },
      className: inspections.length === 0 ? 'hidden' : '',
    },
    timeFilter: {
      controlType: UIControlType.select,
      parse: Number,
      label: 'Filter',
      options: filterOptions,
      style: { flex: '0 1 33%', paddingLeft: '2rem' },
    },
    start_date: {
      controlType: UIControlType.date,
      disabled: (filter.prevMonths || 0) > 0,
      label: 'Start Date',
      style: { flex: '0 1 33%' },
      popperPlacement: 'top',
    },
    end_date: {
      controlType: UIControlType.date,
      disabled: (filter.prevMonths || 0) > 0,
      label: 'End Date',
      style: { flex: '0 1 33%' },
      popperPlacement: 'top',
      validation: {
        maxDate: new Date(),
      },
    },
    siteMapText: {
      controlType: UIControlType.plainText,
      label: siteMapTextLabel,
      style: { paddingLeft: '2rem', marginBottom: '1rem' },
    },
    currentVersion: {
      controlType: UIControlType.hidden,
    },
    [versions.length ? 'versionIds' : 'inspectionIds']: {
      controlType: UIControlType.radio,
      options: choices.map((choice) => ({
        ...choice,
        label: choice.label,
        value: choice.value.toString() + '|' + choice.label,
      })),
      validation: { required: true },
      style: { paddingLeft: '2rem' },
      className:
        choices.length === 0
          ? 'hidden'
          : 'form-group--checkboxGroup two-columns',
    },
  };
};

const isVersionInRange = (
  date: string,
  { prevMonths, start, end }: InspectionDateFilter,
) => {
  const version = dayjs.utc(date);

  return prevMonths
    ? !version.isBefore(dayjs().subtract(prevMonths, 'month'), 'day')
    : (!start || !version.isBefore(start, 'day')) &&
        (!end || !version.isAfter(end, 'day'));
};

/**
 * SW2-2345 — the versions offered here are the ones the map editor offers.
 *
 * This used to list `mapVersions[0].versions` as `v1`, `v2`, and only when the project
 * had no site map inspections at all; everywhere else it listed inspections. So the
 * revisions a user can actually select in the editor were unreachable when printing, and
 * "This Version's Log Only" could not name them.
 *
 * It does NOT rebuild the list. An earlier attempt copied the editor's builder, and the
 * copy had drifted within two days — 210 entries against the editor's 203 on one map,
 * ordered by inspection rather than by date, with none of the labels. Both call
 * `buildVersionOptions` now, so a date picked here is a date the user saw there.
 *
 * The value keeps the inspection id in its first segment, which is what decides the
 * findings a version shows; the day follows it.
 */
const getVersionOptions = (
  mapVersions: MapVersion[],
  filter: InspectionDateFilter,
  formTypeId?: number | string,
): OptionShape[] =>
  buildVersionOptions(scopeVersionsToFormType(mapVersions, formTypeId), {
    // One row per day here. The rescue that keeps an otherwise-unreachable inspection
    // matters in the editor, where you draw against it; on a print it renders the same
    // map and the same changelog as the row already on that day.
    keepUnreachableInspections: false,
  })
    .filter((option) => {
      const day = versionDayOf(option.value);

      return day !== '' && isVersionInRange(day, filter);
    })
    .map((option) => ({ value: option.value, label: option.label }));

/**
 * Client-side filter: given all site map inspections and a selected template type id,
 * return only inspections whose template matches that type.
 *
 * The synthetic "Current Version" entry is kept unconditionally — it is not an
 * inspection and belongs to no form, so there is nothing for a form type to match it
 * against. It is dropped only when no form type is selected at all, which returns
 * nothing. (The previous wording said "when there's a match", which is not what the
 * code does and would mislead anyone changing this filter.)
 */
function filterSiteMapInspectionsByFormType(
  allInspections: any[],
  selectedFormType: string | number | undefined,
): any[] {
  if (!selectedFormType) {
    return [];
  }

  const typeId = Number(selectedFormType);

  // Filter inspections by template type, but always keep "Current Version" entries
  const filtered = allInspections.filter((insp: any) => {
    // "Current Version" entries have type === "Current Version" and no template
    if (insp.type === 'Current Version') {
      return true;
    }

    return insp.inspection_template?.inspection_type_id === typeId;
  });

  // If only "Current Version" entries remain (no actual inspections matched), 
  // still return them as they represent the current state
  return filtered;
}

const getPrintSiteMapsSchema = (
  options: SiteMapSchemaOptions & {
    formTypeOptions: { label: string; value: number | string }[];
    selectedFormType: string | number | undefined;
    currentVersionInspection?: { id: number; created_date: any };
    isGISEnabled?: boolean;
    /** SW2-2345 — drives the changelog scope radio's visibility. */
    formData?: { include_map_changelog?: boolean } & Record<string, any>;
  },
) => {
  const schema: FormSchema = {
    ...getMapOptionsSchema(
      options.targetDocId,
      options.targetDoc,
      options.projectLevelEsriEnabled,
      options.isGISEnabled,
      options.formData,
    ),
    include_archive_map: {
      label: 'Include Archived Maps',
      controlType: UIControlType.checkbox,
      style: {},
    },
  };

  // Always show form type selector
  if (options.formTypeOptions.length > 0) {
    schema.siteMapFormType = {
      controlType: UIControlType.select,
      label: 'Form Type*',
      options: options.formTypeOptions,
      validation: { required: true },
      placeholder: 'Select a form type',
    };
  }

  // Only show site map selection when a form type is selected
  if (options.selectedFormType) {
    Object.assign(schema, getSiteMapFilterSchema(options));
  }

  return schema;
};

export interface IPrintSiteMapsModal {
  onClose: MouseEventHandler<Element>;
  targetDoc?: any;
  isGISEnabled?: boolean;
}

const PrintSiteMapsModal: React.FC<IPrintSiteMapsModal> = ({
  onClose,
  targetDoc,
  isGISEnabled
}) => {
  const projectContext = useContext(ProjectContext);
  const projectPrintContext = useContext(ProjectPrintContext);
  const [formTypeOptions, setFormTypeOptions] = useState<{ label: string; value: number | string }[]>([]);

  const targetDocId = targetDoc ? targetDoc.id : 0;

  const loadFormTypes = async () => {
    const projectId = projectContext.project?.id;

    if (!projectId) return;

    try {
      // SW2-2345 — only the forms this project has maps for. The shared endpoint lists
      // every form the project runs, which offered forms whose print drew another form's
      // maps or nothing at all.
      const templateTypes = await projectSiteMapInspectionApi.formTypes(projectId);

      if (templateTypes && templateTypes.length > 0) {
        const options = templateTypes.map((t) => ({
          label: t.name,
          value: t.id,
        }));

        setFormTypeOptions(options);
      } else {
        setFormTypeOptions([]);
      }
    } catch {
      setFormTypeOptions([]);
    }
  };

  useEffect(() => {
    loadFormTypes();
  }, []);

  // Hooks must run on every render, before any early return
  const projectId = projectContext.project?.id ?? 0;

  const inspectionsSiteMapQuery = useProjectSiteMapInspections(
    projectId,
    targetDocId,
  );

  const allInspectionSiteMapQuery = useProjectSiteMapInspections(
    projectId,
    0,
  );
  const projectUploadId = targetDoc ? targetDoc.ProjectUploadModel.id : 0;
  const mapVersionsQuery = useQuery<MapVersion[]>(
    ['project', 'map-versions', projectId, projectUploadId],
    () => positionableApi.mapVersions(projectId, projectUploadId),
    /**
     * SW2-2345 — runs even with no targetDoc, where projectUploadId is 0.
     *
     * 0 means "every site map in this project", which the endpoint now answers with the
     * union of their version days. Gating on projectUploadId meant the project-level
     * modal never fetched versions at all, silently fell back to listing inspections,
     * and map edits made outside an inspection could not be printed from here.
     */
    { enabled: Boolean(projectId) },
  );

  if (!projectContext.project || projectContext.inspectionsQuery.isFetching) {
    return <LoadingModal show />;
  }

  if (inspectionsSiteMapQuery.isFetching || mapVersionsQuery.isLoading) {
    return <LoadingModal show />;
  }

  const validateInspectionData = (formData: PrintProjectFormData) => {
    let validatedFormData = JSON.parse(JSON.stringify(formData));

    /**
     * SW2-2345 — a version chosen under one form must not survive a switch to another.
     *
     * The radio redraws with the new form's days and nothing looks selected, but the old
     * value is still in the form object: `PrintModal` submits `formContext.value` whole
     * and lib-form does not drop a value whose option has gone. The version branch below
     * tests `versionIds` first, so the stale value won the submit and the print drew the
     * previous form's maps, discarding whatever had just been picked.
     *
     * Harmless before this ticket — the version list did not change with the form, so a
     * retained value was always still valid. It is the narrowing that made it reachable.
     *
     * Rebuilt here rather than read from the render prop because that is where the option
     * list lives and this function cannot see it. Skipped entirely when the version query
     * has no data: a failed or evicted fetch is not evidence that the user's selection is
     * invalid, and treating it as such throws away a good pick.
     */
    /**
     * SW2-2345 — a held version value is only trustworthy while the version radio is
     * the field on screen.
     *
     * `getSiteMapFilterSchema` keys the radio on `versions.length`, so switching to a
     * form with no version days re-renders it as `inspectionIds` while the previous
     * `versionIds` stays in `formContext.value`. Deciding that first, once, keeps every
     * branch below from trusting a value the user can no longer see — including the
     * "Current Version" test, which used to run ahead of this and let a stale pick
     * overwrite the inspection someone had just chosen.
     */
    const offeredVersions = mapVersionsQuery.data
      ? getVersionOptions(
          mapVersionsQuery.data,
          {
            start: validatedFormData.start_date,
            end: validatedFormData.end_date,
            prevMonths: validatedFormData.timeFilter,
          },
          validatedFormData.siteMapFormType,
        )
      : null;

    // `null` means the version query has no data, which is not evidence that the user's
    // selection is invalid — leave it alone rather than throwing away a good pick.
    if (offeredVersions && offeredVersions.length === 0) {
      delete validatedFormData.versionIds;
    }

    /**
     * SW2-2345 — "Current Version" is pinned into the version radio but is not a version.
     *
     * It carries no day, so it must be recognised before the version branch below, which
     * would otherwise read the label's first segment as one. It routes the old way: the
     * inspection id it borrows, plus the flag the report reads to mean "as it stands now".
     */
    if (
      validatedFormData.versionIds
      && String(validatedFormData.versionIds).includes(CURRENT_VERSION_LABEL)
    ) {
      validatedFormData.currentVersion = true;
      validatedFormData.inspectionIds = [
        String(validatedFormData.versionIds).split('|')[0],
      ];
      delete validatedFormData.versionIds;

      return validatedFormData;
    }

    if (validatedFormData.versionIds && offeredVersions) {
      const stillOffered = offeredVersions.some((option) =>
        String(validatedFormData.versionIds).startsWith(`${option.value}|`),
      );

      if (!stillOffered) {
        // The version radio is in play and the held value is not one of its options, so
        // neither half of the selection refers to anything the user can currently see.
        delete validatedFormData.versionIds;
        delete validatedFormData.inspectionIds;
      }
    }

    if (validatedFormData.versionIds) {
      /**
       * SW2-2345 — the radio value is `<inspectionId>|<day>|<label>`: the option value
       * built by `buildVersionOptions`, with the label appended by the schema to keep
       * entries distinct when several inspections share a day.
       *
       * Both halves travel. The day is what the map is read "as of"; the inspection is
       * what decides which findings render on it, and it used to be hardcoded to -1
       * because the only versions ever offered belonged to the no-inspection bucket.
       * Now that every project gets the real list, sending -1 would print a version of
       * the map with none of its inspection's findings.
       */
      const [inspectionId, day] = String(validatedFormData.versionIds).split('|');

      validatedFormData.versionIds = [day];
      validatedFormData.inspectionIds = [inspectionId];

      return validatedFormData;
    }

    // Nothing selected — either the form was switched away from the chosen version
    // above, or there was never an inspection to read. Returning early keeps this from
    // throwing on a missing value; the job then fails instead of printing a guess.
    // `initialFormData` seeds this as `{}`, which is truthy — testing for absence alone
    // let an untouched form reach `.split` below and throw inside PrintModal's unhandled
    // `.then`, leaving the dialog stuck on "requested" with no job and no message.
    if (typeof validatedFormData.inspectionIds !== 'string' || !validatedFormData.inspectionIds) {
      return validatedFormData;
    }

    if (validatedFormData.inspectionIds.split('|')[2]?.trim() === CURRENT_VERSION_LABEL) {
      validatedFormData.currentVersion = true;
    }

    validatedFormData.inspectionIds = [validatedFormData.inspectionIds.split('|')[0]];

    return validatedFormData;
  };

  return (
    <PrintModal
      jobName={'sitemaps'}
      className='print-inspections-modal'
      jobData={{
        projectId: projectContext.project.id,
        targetDocId: targetDoc ? targetDoc.id : null,
      }}
      statusUpdateKey={projectContext.project.id}
      onClose={onClose}
      formSchema={(formContextValue: PrintProjectFormData) => {
        const filter: InspectionDateFilter = {
          start: formContextValue.start_date,
          end: formContextValue.end_date,
          prevMonths: formContextValue.timeFilter,
        };

        const selectedFormType = formContextValue.siteMapFormType;
        const siteMapInspections = inspectionsSiteMapQuery.data || [];
        /**
         * Every project gets the version list now, not only one with no inspections at
         * all: "This Version's Log Only" has to name something the user can select.
         *
         * Narrowed to the selected form. A project can hold one map set per form, and
         * the print resolves which maps to draw from the form the chosen day belongs to
         * — so an unfiltered list let a user pick Form A and be handed Form B's maps.
         */
        const versions = getVersionOptions(
          mapVersionsQuery.data || [],
          filter,
          selectedFormType,
        );
        // Filter by selected form type
        const currentVersionInspection = (siteMapInspections as any[]).find(
          (insp) => insp?.type === CURRENT_VERSION_LABEL,
        );
        const typeFilteredInspections = filterSiteMapInspectionsByFormType(
          siteMapInspections,
          selectedFormType,
        );

        const inspections = projectPrintContext.getInspectionOptions(
          typeFilteredInspections,
          filter,
        );

        // Fallback: if no inspections match for the specific site map, try all site maps
        const fallbackInspections = inspections.length > 0
          ? inspections
          : selectedFormType
            ? projectPrintContext.getInspectionOptions(
                filterSiteMapInspectionsByFormType(
                  allInspectionSiteMapQuery.data?.slice(0, 1) || [],
                  selectedFormType,
                ),
                filter,
              )
            : [];

        return getPrintSiteMapsSchema({
          formData: formContextValue,
          inspections: fallbackInspections,
          versions,
          filter,
          filterOptions: projectPrintContext.timeFilterOptions,
          // Versions are what the radio offers whenever there are any, so the summary
          // line describes those rather than the inspection list behind them.
          siteMapTextLabel: versions.length
            ? projectPrintContext.getSiteMapText({ inspections: versions, filter })
            : selectedFormType
              ? projectPrintContext.getSiteMapText({
                  inspections: fallbackInspections,
                  filter,
                })
              : 'Select a form type to view site maps',
          targetDocId,
          currentVersionInspection,
          formTypeOptions,
          selectedFormType,
          targetDoc,
          projectLevelEsriEnabled: projectContext.project?.esri_layers_enabled,
          isGISEnabled,
        });
      }}
      initialFormData={{
        inspectionIds: {},
        include_findings: false,
        projectId: projectContext.project.id,
        timeFilter: 3,
        currentVersion: false,
        include_map_changelog: false,
        include_legend_item_tags: false,
        include_blank_maps: false,
        include_esri_layers: false,
        // SW2-2345. Set up front so the radio has a selection the moment it appears,
        // and so an unchanged form still posts the documented default.
        change_log_scope: 'all',
        legend_item_tag_scope: 'all',
      }}
      modalTitle='Print Site Maps'
      validateData={validateInspectionData}
    />
  );
};

export default PrintSiteMapsModal;
